import { Button, Form, Tabs } from "antd";
import { FormInstance } from "antd/lib";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ZSpecCommon } from "src/business/specification";
import { SysDataFormItems } from "src/business/sysData/SysDataFormItems";
import { ZWorkflow } from "src/business/workflow/ZWorkflow";
import { mdmDeepEqual } from "src/common/mdmDeepEqual";
import { onError } from "src/common/onError";
import { LoaderBox } from "src/components/LoaderBox";
import { msgQueueStore } from "src/components/MsgQueue";
import { PageBlocker } from "src/components/PageBlocker";
import { PageLayout } from "src/components/PageLayout";
import { PageSubheader } from "src/components/PageSubheader";
import { ToolBox2 } from "src/components/ToolBox2";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

import { WorkflowCardMainTab } from "./tabs/WorkflowCardMainTab";
import { WorkflowCardStepsTab } from "./tabs/WorkflowCardStepsTab";
import { WorkflowCardActions } from "./WorkflowCardActions";
import * as styles from "./WorkflowCardPage.module.less";
import {
  TabId,
  workflowCardPageStore as pageStore,
} from "./workflowCardPageStore";

export const WorkflowCardPage: React.FC = observer(() => {
  const { id = "" } = useParams();
  React.useEffect(() => {
    pageStore.init(id).catch(onError);
  }, [id]);
  const [form] = Form.useForm();
  return (
    <PageLayout title={pageStore.crumbs}>
      <LoaderBox
        remoteData={pageStore.data}
        drawReady={({ workflow, specs }) => (
          <Inner workflow={workflow} form={form} specs={specs} />
        )}
      />
    </PageLayout>
  );
});

const simple = (src: unknown): unknown => JSON.parse(JSON.stringify(src));

type PropsInner = {
  form: FormInstance;
  workflow: ZWorkflow;
  specs: ZSpecCommon[];
};
const Inner: React.FC<PropsInner> = observer(({ workflow, form, specs }) => {
  const navigate = useNavigate();
  const [srcValues, setSrcValues] = React.useState<unknown>();
  React.useEffect(() => {
    form.resetFields();
    form.setFieldsValue(workflow);
    setSrcValues(simple(form.getFieldsValue()));
    pageStore.setHasChanges(false);
  }, [workflow]);
  const onValuesChange = (_: unknown, values: unknown) => {
    pageStore.setHasChanges(!mdmDeepEqual(srcValues, simple(values)));
  };
  const { isNew, curTab, hasChanges } = pageStore;
  const submitText = isNew ? "Создать" : "Сохранить";
  const onSuccess: (id: string) => void = isNew
    ? (id) => {
        msgQueueStore.add({
          type: "success",
          message: "Выполнено создание рабочего потока",
        });
        pageStore.setHasChanges(false);
        // редирект на страницу редактирования
        navigate(makeUrl(PageUrl.managementWorkflowCard, { id }), {
          replace: true,
        });
      }
    : () => {
        msgQueueStore.add({ type: "success", message: "Данные сохранены" });
      };
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => pageStore.save(values, onSuccess)}
      onFinishFailed={({ errorFields }) => {
        const err = errorFields[0];
        msgQueueStore.add({
          type: "error",
          message: err
            ? `${err.name}: ${err.errors.join(", ")}`
            : "Форма заполнена с ошибками",
        });
      }}
      className={styles.form}
      onValuesChange={onValuesChange}
    >
      <PageSubheader title={pageStore.title}>
        {hasChanges && "*"}
        {pageStore.isActionsVisible && <WorkflowCardActions />}
        <Button type="primary" htmlType="submit">
          {submitText}
        </Button>
      </PageSubheader>

      <ToolBox2
        paddingMode="page"
        mainTool={
          <Tabs
            items={pageStore.tabOptions}
            activeKey={curTab}
            onChange={(key) => pageStore.setCurTab(key as TabId)}
          />
        }
      >
        <WorkflowCardMainTab
          isNew={isNew}
          hidden={curTab !== "main"}
          initialData={workflow}
        />
        {!isNew && (
          <WorkflowCardStepsTab
            hidden={curTab !== "steps"}
            specs={specs}
            onChange={() => onValuesChange({}, form.getFieldsValue())}
          />
        )}
        <div style={{ display: curTab !== "sys" ? "none" : undefined }}>
          <SysDataFormItems data={workflow} />
        </div>
      </ToolBox2>

      <PageBlocker changed={() => pageStore.hasChanges} />
    </Form>
  );
});
